import { useState, useRef, useCallback, useEffect, useMemo } from "react"
import { PostType, CAROUSEL_TOTAL_MAX_BYTES } from "../types/scheduled-post"
import { validateMediaFile, getMediaDimensions, MediaError } from "../lib/validateMediaFile"
import { uploadFileDirect } from "../lib/directUpload"

export type ExistingMedia = {
  kind: "existing"
  id: number
  url: string
  content_type: string
  byte_size: number
}

export type NewMedia = {
  kind: "new"
  file: File
  previewUrl: string
  errors: MediaError[]
  warnings: MediaError[]
  validating: boolean
  signedId: string | null
  uploading: boolean
  uploadError: string | null
}

export type MediaItem = ExistingMedia | NewMedia

// 後方互換エイリアス（usePostForm.test.ts が参照しているため残置）
export type FileWithErrors = NewMedia

// カルーセルのアスペクト比取得（ExistingMedia は URL から、NewMedia は File から）
const getItemRatio = async (item: MediaItem): Promise<number | null> => {
  const isVideo = item.kind === "existing"
    ? item.content_type.startsWith("video/")
    : item.file.type.startsWith("video/")
  const src = item.kind === "existing" ? item.url : item.file
  const dims = await getMediaDimensions(src, isVideo)
  return dims && dims.height > 0 ? dims.width / dims.height : null
}

export const useFileUpload = (postType: PostType, initialAttachments: ExistingMedia[] = []) => {
  const [items, setItems] = useState<MediaItem[]>(initialAttachments)
  const [removedIds, setRemovedIds] = useState<number[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const itemsRef = useRef<MediaItem[]>(items)
  const prevPostTypeRef = useRef<PostType>(postType)
  const uploadingFilesRef = useRef<Set<File>>(new Set())

  useEffect(() => { itemsRef.current = items }, [items])

  // 投稿タイプ変更時に新規ファイルのみ再バリデーション
  useEffect(() => {
    if (prevPostTypeRef.current === postType) return
    prevPostTypeRef.current = postType

    const current = itemsRef.current
    const hasNew = current.some(i => i.kind === "new")
    if (!hasNew) return

    setItems(current.map(item =>
      item.kind === "new" ? { ...item, errors: [], warnings: [], validating: true } : item
    ))

    const newItems = current.filter((i): i is NewMedia => i.kind === "new")
    const newOffsets = current.reduce<number[]>((acc, item, idx) => {
      if (item.kind === "new") acc.push(idx)
      return acc
    }, [])

    ;(async () => {
      let referenceRatio: number | undefined
      if (postType === "carousel" && current.length > 0) {
        const ratio = await getItemRatio(current[0])
        if (ratio !== null) referenceRatio = ratio
      }

      const results = await Promise.all(
        newItems.map((item, i) => {
          const globalIdx = newOffsets[i]
          const ref = postType === "carousel" && globalIdx > 0 ? referenceRatio : undefined
          return validateMediaFile(item.file, postType, globalIdx, ref)
        })
      )

      const resultMap = new Map(newItems.map((item, i) => [item.file, results[i]]))
      setItems(prev => prev.map(item => {
        if (item.kind !== "new") return item
        const result = resultMap.get(item.file) ?? { errors: [], warnings: [] }
        return { ...item, errors: result.errors, warnings: result.warnings, validating: false }
      }))
    })()
  }, [postType])

  // バリデーション通過後に自動でDirect Uploadを開始する
  useEffect(() => {
    items.forEach(item => {
      if (item.kind !== "new") return
      if (item.validating || item.errors.length > 0 || item.signedId || item.uploading || item.uploadError) return
      if (uploadingFilesRef.current.has(item.file)) return

      uploadingFilesRef.current.add(item.file)
      setItems(prev => prev.map(i =>
        i.kind === "new" && i.file === item.file ? { ...i, uploading: true } : i
      ))

      uploadFileDirect(item.file)
        .then(signedId => {
          setItems(prev => prev.map(i =>
            i.kind === "new" && i.file === item.file ? { ...i, uploading: false, signedId } : i
          ))
        })
        .catch(() => {
          uploadingFilesRef.current.delete(item.file)
          setItems(prev => prev.map(i =>
            i.kind === "new" && i.file === item.file
              ? { ...i, uploading: false, uploadError: "アップロードに失敗しました" }
              : i
          ))
        })
    })
  }, [items])

  // クリーンアップ
  useEffect(() => {
    return () => {
      itemsRef.current.forEach(item => {
        if (item.kind === "new") URL.revokeObjectURL(item.previewUrl)
      })
    }
  }, [])

  const validateAndAdd = useCallback(async (newFiles: File[], currentItems: MediaItem[]) => {
    const pending: NewMedia[] = newFiles.map(file => ({
      kind: "new",
      file,
      previewUrl: URL.createObjectURL(file),
      errors: [],
      warnings: [],
      validating: true,
      signedId: null,
      uploading: false,
      uploadError: null,
    }))

    setItems([...currentItems, ...pending])

    // カルーセル: 1枚目からアスペクト比を取得し、2枚目以降のバリデーションに渡す
    let referenceRatio: number | undefined
    if (postType === "carousel") {
      const firstItem = currentItems.length > 0 ? currentItems[0] : pending[0]
      if (firstItem) {
        const ratio = await getItemRatio(firstItem)
        if (ratio !== null) referenceRatio = ratio
      }
    }

    const offset = currentItems.length
    const results = await Promise.all(
      pending.map((item, i) => {
        const globalIdx = offset + i
        const ref = postType === "carousel" && globalIdx > 0 ? referenceRatio : undefined
        return validateMediaFile(item.file, postType, globalIdx, ref)
      })
    )

    setItems(prev => {
      const updated = [...prev]
      const base = prev.length - pending.length
      results.forEach((result, i) => {
        const idx = base + i
        if (updated[idx]?.kind === "new") {
          updated[idx] = { ...(updated[idx] as NewMedia), errors: result.errors, warnings: result.warnings, validating: false }
        }
      })
      return updated
    })
  }, [postType])

  const handleFilesAdded = (newFiles: File[]) => {
    const current = itemsRef.current
    if (postType !== "carousel") {
      if (current.length >= 1) return
      validateAndAdd([newFiles[0]], current)
    } else {
      validateAndAdd(newFiles, current)
    }
  }

  const removeFile = (index: number) => {
    setItems(prev => {
      const item = prev[index]
      if (item.kind === "existing") {
        setRemovedIds(ids => [...ids, item.id])
      } else {
        URL.revokeObjectURL(item.previewUrl)
      }
      return prev.filter((_, i) => i !== index)
    })
  }

  const handleReorder = (fromIndex: number, toIndex: number) => {
    setItems(prev => {
      const updated = [...prev]
      const [moved] = updated.splice(fromIndex, 1)
      updated.splice(toIndex, 0, moved)
      return updated
    })
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleFilesAdded(Array.from(e.dataTransfer.files))
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => setIsDragging(false)

  const carouselSizeError = useMemo<string | null>(() => {
    if (postType !== "carousel") return null
    const total = items.reduce((sum, item) => {
      return sum + (item.kind === "new" ? item.file.size : item.byte_size)
    }, 0)
    if (total > CAROUSEL_TOTAL_MAX_BYTES) {
      const mb = CAROUSEL_TOTAL_MAX_BYTES / (1024 * 1024)
      return `カルーセルの合計サイズが上限（${mb}MB）を超えています`
    }
    return null
  }, [items, postType])

  return {
    items,
    files: items, // 後方互換エイリアス
    removedIds,
    isDragging,
    isValidatingAny: items.some(i => i.kind === "new" && i.validating),
    hasFileErrors: items.some(i => i.kind === "new" && i.errors.length > 0),
    hasWarnings: items.some(i => i.kind === "new" && i.warnings.length > 0),
    isUploadingAny: items.some(i => i.kind === "new" && i.uploading),
    hasUploadErrors: items.some(i => i.kind === "new" && i.uploadError !== null),
    carouselSizeError,
    handleFilesAdded,
    removeFile,
    handleReorder,
    handleDrop,
    handleDragOver,
    handleDragLeave,
  }
}
